/**
 * Pure readings of a run that more than one memo surface needs: which facts a
 * sentence names, what state a section is in for the nav rail, the distinct
 * sources the memo cites, and the fiscal periods the financial summary spans.
 *
 * Kept out of the components so the memo page, the Sources tab and the header
 * count the same citations the same way — "24 citations" in the header and a
 * Sources tab listing 23 would be two answers to one question.
 */

import { stripMarks } from "./inlineMarks";
import type {
	DealScreeningCell,
	DealScreeningFact,
	DealScreeningParagraph,
	DealScreeningRun,
	DealScreeningSection,
	DealScreeningSource,
} from "../../types/dealScreening";
import { formatFactValue, formatTableValue, moneyTextVariants } from "./dealScreeningFormat";

/** The company facts block's heading: as a person renamed it, else the default (DOP-641). */
export const companyFactsTitle = (run: { conventions?: Record<string, string> }): string =>
	run.conventions?.company_facts_title?.trim() || "Company facts";

/** Words a Title Case heading leaves lower-case. */
const MINOR_WORDS = new Set(["a", "an", "and", "at", "by", "for", "in", "of", "on", "or", "the", "to", "with"]);
const CAPITALISED = /^[A-Z][a-z]+$/;

/**
 * A sub-heading in sentence case (DOP-659): "What They Do" reads "What they
 * do", like "Product portfolio" beside it. Only a heading written in Title
 * Case is changed, so one a person typed their own way is left alone;
 * acronyms ("IP") and anything not a plain capitalised word are kept.
 */
export function sentenceCaseHeading(heading: string): string {
	const words = heading.split(" ");
	const real = words.filter((word) => /[A-Za-z]/.test(word));
	const titleCase =
		real.length > 1 &&
		real.every((word, at) => /^[A-Z]/.test(word) || (at > 0 && MINOR_WORDS.has(word)));
	if (!titleCase) return heading;
	let first = true;
	return words
		.map((word) => {
			if (!/[A-Za-z]/.test(word)) return word;
			const keep = first || !CAPITALISED.test(word);
			first = false;
			return keep ? word : word.toLowerCase();
		})
		.join(" ");
}

/** Facts by key; a by-year figure has one fact per period under one key. */
export function factIndex(
	facts: DealScreeningFact[],
): Map<string, DealScreeningFact[]> {
	const index = new Map<string, DealScreeningFact[]>();
	for (const fact of facts) {
		index.set(fact.key, [...(index.get(fact.key) ?? []), fact]);
	}
	return index;
}

/**
 * The block groups still open on a run: every member of the group is a gap.
 * The server's `counts_from` rule, so the memo says "blocks" exactly where the
 * header pill counts one. A field with no group is its own group.
 */
export function openBlockGroups(facts: DealScreeningFact[]): Set<string> {
	const groups = new Map<string, boolean>();
	for (const fact of facts) {
		if (fact.policy !== "blocks" || fact.kind === "derived") continue;
		const group = fact.block_group ?? fact.key;
		if (!groups.has(group)) groups.set(group, true);
		if (fact.state !== "gap") groups.set(group, false);
	}
	return new Set(
		Array.from(groups.entries())
			.filter(([, open]) => open)
			.map(([group]) => group),
	);
}

/** Section keys the product dropped; a stored run may still carry them. */
const RETIRED_SECTION_KEYS = new Set(["source_note"]);

/** The key of the section the run's questions are written into. */
export const QUESTIONS_SECTION_KEY = "diligence_questions";

/**
 * The memo's sections in reading order. A run generated before a section was
 * retired still stores it (the Source note, DOP-523); it is not part of the
 * memo any more.
 */
export function memoSections(sections: DealScreeningSection[]): DealScreeningSection[] {
	return sections
		.filter((section) => !RETIRED_SECTION_KEYS.has(section.key))
		.sort((a, b) => a.order - b.order);
}

/** "1", "2" … for the fixed outline; "4a" for a deal-specific section after 4. */
export function sectionNumbers(sections: DealScreeningSection[]): Map<string, string> {
	const numbers = new Map<string, string>();
	let core = 0;
	let extra = 0;
	for (const section of sections) {
		if (section.deal_specific) {
			extra += 1;
			numbers.set(section.key, `${core}${String.fromCharCode(96 + extra)}`);
		} else {
			core += 1;
			extra = 0;
			numbers.set(section.key, String(core));
		}
	}
	return numbers;
}

/**
 * Where this memo writes its questions, by what the section is about:
 * "Questions / Areas for Diligence". Every gap's question lands there,
 * whichever sections the gap itself touches; a number ("section 9") moves
 * with the workflow's outline and tells a reader nothing (DOP-573).
 */
export function questionsSectionName(sections: DealScreeningSection[]): string {
	const section = sections.find((s) => s.key === QUESTIONS_SECTION_KEY);
	return section ? `“${stripMarks(section.title)}”` : "the diligence questions";
}

export type SectionDot = "sourced" | "conflict" | "gap";

/** The cells of a table paragraph, flattened; none for prose or bullets. */
export const tableCells = (paragraph: DealScreeningParagraph): DealScreeningCell[] =>
	paragraph.kind === "table" && paragraph.table ? paragraph.table.rows.flat() : [];

/**
 * The nav rail's dot for a section: a gap (omitted, or naming a missing
 * fact) outranks a conflict, which outranks "all sourced".
 */
export function sectionDot(
	section: DealScreeningSection,
	run: DealScreeningRun,
	index: Map<string, DealScreeningFact[]>,
): SectionDot {
	if (section.status === "omitted") return "gap";
	let named: DealScreeningFact[];
	if (section.key === "financial_summary") {
		named = run.facts.filter((fact) => fact.kind !== "header");
	} else {
		const keys = new Set(
			section.paragraphs.flatMap((paragraph) => [
				...paragraph.sentences.flatMap((sentence) => sentence.fact_keys),
				...tableCells(paragraph).flatMap((cell) => cell.fact_keys),
			]),
		);
		named = Array.from(keys).flatMap((key) => index.get(key) ?? []);
	}
	// A table's label cells carry no source by design; the verifier already
	// dropped any figure cell it could not source, so cells never count as gaps.
	// A sentence a person wrote has no source either, and is theirs, not a gap:
	// the Gaps tab never lists it, so the rail must not flag it.
	const unsourced = section.paragraphs.some((paragraph) =>
		paragraph.sentences.some(
			(sentence) =>
				sentence.source === null &&
				!(sentence.edited ?? paragraph.edit != null),
		),
	);
	if (unsourced || named.some((fact) => fact.state === "gap")) return "gap";
	if (named.some((fact) => fact.conflict === "open")) return "conflict";
	return "sourced";
}

/** The memo element a section is drawn in. */
export const sectionElementId = (key: string): string => `memo-section-${key}`;
/** The memo element a company fact (or the tagline) is drawn in. */
export const factElementId = (key: string): string => `memo-fact-${key}`;

/** Where on the memo a cited place is used: what to scroll to. */
export interface SourceTarget {
	/** "Target markets (also stated)", "Market Opportunity". */
	label: string;
	/** The memo element that holds it. */
	elementId: string;
	/** A company fact whose other readings must be unfolded to show it. */
	expandFact?: string;
}

/** One cited place: a page or a cell, and what on the memo points at it. */
export interface SourceUse {
	/** The anchor, which is unique per page-and-quote or per cell. */
	key: string;
	source: DealScreeningSource;
	uses: string[];
	/** The same uses, as places on the memo a reader can jump to. */
	targets: SourceTarget[];
}

/** A fact's place on the memo: its row in the company facts, or the financial summary. */
const factElement = (fact: DealScreeningFact): string =>
	fact.kind === "header" ? factElementId(fact.key) : sectionElementId("financial_summary");

const locationLabel = (fact: DealScreeningFact): string =>
	fact.period ? `${fact.label} ${fact.period}` : fact.label;

/**
 * Every distinct source the run cites — sentences, facts, the alternates of a
 * conflict and the Analyst's questions — with where each is used.
 */
export function distinctSources(run: DealScreeningRun): SourceUse[] {
	const byKey = new Map<string, SourceUse>();
	const add = (
		source: DealScreeningSource | null,
		use: string,
		elementId: string,
		expandFact?: string,
	) => {
		if (!source) return;
		const target: SourceTarget = { label: use, elementId, ...(expandFact ? { expandFact } : {}) };
		const existing = byKey.get(source.anchor);
		if (existing) {
			if (!existing.uses.includes(use)) {
				existing.uses.push(use);
				existing.targets.push(target);
			}
			return;
		}
		byKey.set(source.anchor, { key: source.anchor, source, uses: [use], targets: [target] });
	};
	for (const fact of run.facts) {
		add(fact.source, locationLabel(fact), factElement(fact));
		fact.alternates.forEach((alt) =>
			add(
				alt.source,
				`${locationLabel(fact)} (also stated)`,
				factElement(fact),
				fact.kind === "header" ? fact.key : undefined,
			),
		);
	}
	for (const section of run.sections) {
		const title = stripMarks(section.title);
		const element = sectionElementId(section.key);
		section.paragraphs.forEach((paragraph) => {
			paragraph.sentences.forEach((sentence) => add(sentence.source, title, element));
			tableCells(paragraph).forEach((cell) => add(cell.source, title, element));
		});
	}
	for (const question of run.questions) {
		add(question.source, `Question: ${question.topic}`, sectionElementId(QUESTIONS_SECTION_KEY));
	}
	return Array.from(byKey.values()).sort(
		(a, b) =>
			a.source.filename.localeCompare(b.source.filename) ||
			(a.source.page ?? 0) - (b.source.page ?? 0) ||
			(a.source.cell ?? "").localeCompare(b.source.cell ?? ""),
	);
}

const periodYear = (period: string): number => {
	const match = /(\d{4})/.exec(period);
	return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

/** The fiscal periods the facts span, oldest first: FY2022A … FY2027P. */
export function periodsOf(facts: DealScreeningFact[]): string[] {
	const periods = new Set<string>();
	facts.forEach((fact) => {
		if (fact.period) periods.add(fact.period);
	});
	return Array.from(periods).sort(
		(a, b) => periodYear(a) - periodYear(b) || a.localeCompare(b),
	);
}

/** A run of sentence text, and the fact whose value it is, when it is one. */
export interface TextSegment {
	text: string;
	fact: DealScreeningFact | null;
}

/**
 * Split a sentence into plain text and the values of the facts it names, so
 * those values can be underlined and opened at their own cell.
 *
 * Matching is by the value as the memo writes it ("$13.2mm", "36%"): the
 * composer states figures in the recipe's units convention, and a value that
 * does not appear verbatim is simply not underlined — the sentence still
 * carries its own source. Never guessed from a looser match: underlining the
 * wrong "13.2" would send the reader to the wrong cell.
 */
export function segmentSentence(
	text: string,
	facts: DealScreeningFact[],
): TextSegment[] {
	const hits: Array<{ start: number; end: number; fact: DealScreeningFact }> = [];
	for (const fact of facts) {
		if (fact.value === null || typeof fact.value !== "number") continue;
		const candidates = Array.from(
			new Set([
				formatFactValue(fact.value, fact.unit),
				formatTableValue(fact.value, fact.unit),
				// A memo written before DOP-636 printed money to two decimals.
				...moneyTextVariants(fact.value, fact.unit),
			]),
		).filter((candidate) => candidate.length >= 2);
		for (const candidate of candidates) {
			let from = 0;
			while (from < text.length) {
				const start = text.indexOf(candidate, from);
				if (start === -1) break;
				const end = start + candidate.length;
				// A whole token only: "3.2" must not match inside "13.2".
				const before = start === 0 ? " " : text[start - 1];
				const after = end >= text.length ? " " : text[end];
				const bounded = !/[\d.$]/.test(before) && !/\d/.test(after);
				const overlaps = hits.some((hit) => start < hit.end && end > hit.start);
				if (bounded && !overlaps) {
					hits.push({ start, end, fact });
					break;
				}
				from = start + 1;
			}
		}
	}
	hits.sort((a, b) => a.start - b.start);
	const segments: TextSegment[] = [];
	let cursor = 0;
	for (const hit of hits) {
		if (hit.start > cursor) {
			segments.push({ text: text.slice(cursor, hit.start), fact: null });
		}
		segments.push({ text: text.slice(hit.start, hit.end), fact: hit.fact });
		cursor = hit.end;
	}
	if (cursor < text.length) segments.push({ text: text.slice(cursor), fact: null });
	return segments;
}

/**
 * The number each cited place carries in the memo (DOP-616), like a
 * footnote: 1 is the first source the reader meets. Numbering walks the memo
 * in reading order: sections, then their sentences and table cells, then the
 * questions. Facts cited only in the header block or the financial table
 * come last. The Files read tab shows the same numbers, so "4" names one
 * page everywhere.
 */
export function sourceNumbers(run: DealScreeningRun): Map<string, number> {
	const numbers = new Map<string, number>();
	const add = (source: DealScreeningSource | null) => {
		// A person's note is not a file: it opens itself and takes no number (DOP-637).
		if (source && source.kind !== "user" && !numbers.has(source.anchor)) {
			numbers.set(source.anchor, numbers.size + 1);
		}
	};
	for (const section of memoSections(run.sections)) {
		section.paragraphs.forEach((paragraph) => {
			paragraph.sentences.forEach((sentence) => add(sentence.source));
			tableCells(paragraph).forEach((cell) => add(cell.source));
		});
	}
	run.questions.forEach((question) => add(question.source));
	run.facts.forEach((fact) => add(fact.source));
	return numbers;
}
